"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Search, Check, X } from "lucide-react";
import { toDatetimeLocalValue } from "../utils/timezone";
import { getTipCommissionLabel, TIP_ROLES } from "../utils/tipCommission";

export const DealerTipEditModal = ({ 
  isOpen, 
  onClose, 
  record, 
  onUpdate,
  employees,
  calculateCommission,
  currentUser
}) => {
  const [formData, setFormData] = useState({
    date: "",
    empname: "",
    amount: ""
  });
  const [tax, setTax] = useState(0);
  const [paidOutAmount, setPaidOutAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    if (!record) return;

    setFormData({
      date: toDatetimeLocalValue(record.date),
      empname: record.employee || "",
      amount: record.tip_out_cash || ""
    });
  }, [record]);

  useEffect(() => {
    const amt = parseFloat(formData.amount) || 0;
    const transactionDate = formData.date || record?.date;

    if (!formData.amount || !transactionDate) {
      setTax(0);
      setPaidOutAmount(0);
      return;
    }

    const calculatedTax = calculateCommission(amt, transactionDate);
    setTax(calculatedTax);
    setPaidOutAmount(amt - calculatedTax);
  }, [formData.amount, formData.date, record?.date, calculateCommission]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter employees with proper null/undefined checks
  const filteredEmployees = useMemo(() => {
    if (!employees || !Array.isArray(employees)) {
      return [];
    }
    
    return employees.filter(emp => {
      // Handle both string and object formats
      if (typeof emp === 'string') {
        return emp.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (emp && typeof emp === 'object' && emp.name) {
        return emp.name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });
  }, [employees, searchTerm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmployeeSelect = (emp) => {
    // Handle both string and object formats
    const employeeName = typeof emp === 'string' ? emp : emp.name;
    setFormData(prev => ({ ...prev, empname: employeeName }));
    setDropdownOpen(false);
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSelection = () => {
    setFormData(prev => ({ ...prev, empname: "" }));
  };

  const handleSubmit = async () => {
    if (!record) return;
    
    setSubmitting(true);
    await onUpdate({
      id: record.id,
      date: formData.date,
      empname: formData.empname,
      amount: parseFloat(formData.amount)
    });
    
    setSubmitting(false);
    onClose();
  };

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Record</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name
            </label>
            
            <div className="relative" ref={dropdownRef}>
              <div 
                className="flex items-center justify-between border rounded-md p-2 cursor-pointer bg-white"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {formData.empname ? (
                  <div className="flex items-center">
                    <span className="mr-2">{formData.empname}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg 
                  className={`w-4 h-4 ml-2 transform transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              
              {dropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => {
                        const employeeName = typeof emp === 'string' ? emp : emp.name;
                        const employeeKey = typeof emp === 'string' ? emp : emp.id || emp.name || index;
                        
                        return (
                          <div
                            key={employeeKey}
                            className={`flex items-center p-2 cursor-pointer hover:bg-gray-100 ${
                              formData.empname === employeeName ? "bg-gray-100" : ""
                            }`}
                            onClick={() => handleEmployeeSelect(emp)}
                          >
                            <div className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                              formData.empname === employeeName 
                                ? "bg-blue-500 border-blue-500" 
                                : "border-gray-300"
                            }`}>
                              {formData.empname === employeeName && (
                                <Check className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <span>{employeeName}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500">
                        No employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <Input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className="w-full"
              step="0.01"
              min="0"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {getTipCommissionLabel(TIP_ROLES.DEALER, formData.date || record?.date)}
              </label>
              <div className="text-lg text-red-500 font-semibold">
                -${tax.toFixed(2)}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Out
              </label>
              <div className="text-lg text-green-500 font-semibold">
                ${paidOutAmount.toFixed(2)}
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || "Current User"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};